import createClient from "openapi-fetch";
import type { paths, components } from "@trafficplace/api-client";

import { getInitData } from "../../../lib/tg";

// Клиент openapi-fetch с авторизацией через Telegram initData
const client = createClient<paths>({
  baseUrl: import.meta.env.VITE_API_URL,
});

// Inline-тип для создания инвойса (T38/sponsor deposit).
// TODO(Phase 2c): перевести finance endpoints на typed openapi-fetch клиент
// (после T53 типы доступны в schema.ts, но миграция отложена в отдельный слайс).
// API: TopupInvoiceIn { amount: Decimal }
type CreateInvoiceIn = {
  amount: string;
};

// Вспомогательная функция для формирования заголовка авторизации
function authHeaders(): { Authorization: string } {
  return { Authorization: `tma ${getInitData()}` };
}

export type SponsorStatsSnapshot = components["schemas"]["SponsorStatsOut"];

export const sponsorApi = {
  listResources: (signal?: AbortSignal) =>
    client.GET("/resources", { headers: authHeaders(), signal }),

  getResource: (id: number, signal?: AbortSignal) =>
    client.GET("/resources/{resource_id}", {
      params: { path: { resource_id: id } },
      headers: authHeaders(),
      signal,
    }),

  createResource: (body: components["schemas"]["CreateResourceIn"]) =>
    client.POST("/resources", { body, headers: authHeaders() }),

  patchResource: (
    id: number,
    body: components["schemas"]["UpdateResourceIn"],
  ) =>
    client.PATCH("/resources/{resource_id}", {
      params: { path: { resource_id: id } },
      body,
      headers: authHeaders(),
    }),

  patchResourceSettings: (id: number, body: Record<string, unknown>) =>
    fetch(`${import.meta.env.VITE_API_URL}/resources/${id}/settings`, {
      method: "PATCH",
      headers: { ...authHeaders(), "Content-Type": "application/json" },
      body: JSON.stringify(body),
    }).then((r) =>
      r
        .json()
        .then((data) =>
          r.ok ? { data, error: undefined } : { data: null, error: data },
        )
        .catch(() => ({ data: null, error: { detail: "parse error" } })),
    ),

  verifyResource: (id: number) =>
    client.POST("/resources/{resource_id}/verify", {
      params: { path: { resource_id: id } },
      headers: authHeaders(),
    }),

  submitResource: (id: number) =>
    client.POST("/resources/{resource_id}/submit", {
      params: { path: { resource_id: id } },
      headers: authHeaders(),
    }),

  deleteResource: (id: number): Promise<{ data: null; error: unknown }> =>
    fetch(`${import.meta.env.VITE_API_URL}/resources/${id}`, {
      method: "DELETE",
      headers: authHeaders(),
    }).then(async (r) => {
      if (r.ok) return { data: null, error: undefined };
      try {
        return { data: null, error: await r.json() };
      } catch {
        return { data: null, error: { detail: "parse error" } };
      }
    }),

  // Finance endpoints (T38). Типы есть в schema.ts (T53),
  // но миграция fetch -> typed client отложена в Phase 2c.
  getSponsorBalance: (): Promise<{ data: unknown; error: unknown }> =>
    fetch(`${import.meta.env.VITE_API_URL}/finance/balance/sponsor`, {
      headers: authHeaders(),
    }).then((r) =>
      r
        .json()
        .then((data) =>
          r.ok ? { data, error: undefined } : { data: null, error: data },
        )
        .catch(() => ({ data: null, error: { detail: "parse error" } })),
    ),

  listOperations: (): Promise<{ data: unknown; error: unknown }> =>
    fetch(`${import.meta.env.VITE_API_URL}/finance/operations/sponsor`, {
      headers: authHeaders(),
    }).then((r) =>
      r
        .json()
        .then((data) =>
          r.ok ? { data, error: undefined } : { data: null, error: data },
        )
        .catch(() => ({ data: null, error: { detail: "parse error" } })),
    ),

  createInvoice: (
    body: CreateInvoiceIn,
  ): Promise<{ data: unknown; error: unknown }> =>
    fetch(`${import.meta.env.VITE_API_URL}/finance/topup/invoice`, {
      method: "POST",
      headers: { ...authHeaders(), "Content-Type": "application/json" },
      body: JSON.stringify(body),
    }).then((r) =>
      r
        .json()
        .then((data) =>
          r.ok ? { data, error: undefined } : { data: null, error: data },
        )
        .catch(() => ({ data: null, error: { detail: "parse error" } })),
    ),

  getTopupRate: (): Promise<{ data: unknown; error: unknown }> =>
    fetch(`${import.meta.env.VITE_API_URL}/finance/topup-rate`, {
      headers: authHeaders(),
    }).then((r) =>
      r
        .json()
        .then((data) =>
          r.ok ? { data, error: undefined } : { data: null, error: data },
        )
        .catch(() => ({ data: null, error: { detail: "parse error" } })),
    ),

  getStats: (params?: {
    dateFrom?: string;
    dateTo?: string;
    resourceId?: number;
    refresh?: boolean;
  }): Promise<{
    data: SponsorStatsSnapshot | null;
    error: unknown;
    status?: number;
  }> =>
    fetch(
      `${import.meta.env.VITE_API_URL}/sponsor/stats${statsQuery(params)}`,
      {
        headers: authHeaders(),
      },
    ).then((r) =>
      r
        .json()
        .then((data) =>
          r.ok
            ? {
                data: data as SponsorStatsSnapshot,
                error: undefined,
                status: r.status,
              }
            : { data: null, error: data, status: r.status },
        )
        .catch(() => ({
          data: null,
          error: { detail: "parse error" },
          status: r.status,
        })),
    ),

  sendStatsExport: (params?: {
    dateFrom?: string;
    dateTo?: string;
    mode?: "events" | "leads";
    resourceId?: number;
  }): Promise<{ data: unknown; error: unknown }> =>
    fetch(
      `${import.meta.env.VITE_API_URL}/sponsor/stats/export-send${statsQuery(params)}`,
      {
        method: "POST",
        headers: authHeaders(),
      },
    ).then((r) =>
      r
        .json()
        .then((data) =>
          r.ok ? { data, error: undefined } : { data: null, error: data },
        )
        .catch(() => ({ data: null, error: { detail: "parse error" } })),
    ),
};

function statsQuery(params?: {
  dateFrom?: string;
  dateTo?: string;
  mode?: "events" | "leads";
  resourceId?: number;
  refresh?: boolean;
}): string {
  if (!params) return "";
  const query = new URLSearchParams();
  if (params.dateFrom) query.set("date_from", params.dateFrom);
  if (params.dateTo) query.set("date_to", params.dateTo);
  if (params.mode) query.set("mode", params.mode);
  if (params.resourceId) query.set("resource_id", String(params.resourceId));
  if (params.refresh) query.set("refresh", "true");
  const raw = query.toString();
  return raw ? `?${raw}` : "";
}
